import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Bot, FileText, Calendar } from 'lucide-react';
import * as fieldTeamApi from '../../services/fieldTeamApi';
import './FieldTeam.css';

export default function Commission() {
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [sumRes, brkRes] = await Promise.all([
          fieldTeamApi.getCommissionSummary({ period_start: periodStart, period_end: periodEnd }),
          fieldTeamApi.getCommissionBreakdown({ period_start: periodStart, period_end: periodEnd })
        ]);
        setSummary(sumRes.data?.data || null);
        setBreakdown(brkRes.data?.data || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetch();
  }, [periodStart, periodEnd]);

  const ruleLabel = summary?.commission_rule ? (
    summary.commission_rule.type === 'percentage' ? `${(summary.commission_rule.rate * 100).toFixed(1)}% of paid` :
    summary.commission_rule.type === 'per_bot' ? `${summary.commission_rule.rate} per active bot` :
    `${summary.commission_rule.rate} per invoice`
  ) : 'Not configured';

  return (
    <div className="ft-page">
      {/* Period Filter */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
        <Calendar size={16} style={{ color: 'var(--text-muted)' }} />
        <input type="date" className="input" value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={{ width: '160px' }} />
        <span style={{ color: 'var(--text-muted)' }}>to</span>
        <input type="date" className="input" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={{ width: '160px' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading...</div>
      ) : !summary ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>No commission data</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="stat-blocks">
            <div className="stat-block">
              <div className="stat-icon"><DollarSign size={20} /></div>
              <div className="stat-content">
                <span className="stat-value">PKR {summary.total_paid?.toLocaleString()}</span>
                <span className="stat-label">Total Paid ({summary.paid_invoices} invoices)</span>
              </div>
            </div>
            <div className="stat-block">
              <div className="stat-icon"><Bot size={20} /></div>
              <div className="stat-content">
                <span className="stat-value">{summary.active_bots}</span>
                <span className="stat-label">Active Bots</span>
              </div>
            </div>
            <div className="stat-block">
              <div className="stat-icon"><FileText size={20} /></div>
              <div className="stat-content">
                <span className="stat-value">{ruleLabel}</span>
                <span className="stat-label">Commission Rate</span>
              </div>
            </div>
            <div className="stat-block" style={{ '--accent': '#22c55e' }}>
              <div className="stat-icon"><TrendingUp size={20} /></div>
              <div className="stat-content">
                <span className="stat-value" style={{ color: '#22c55e' }}>PKR {summary.estimated_commission?.toLocaleString()}</span>
                <span className="stat-label">Estimated Commission</span>
              </div>
            </div>
          </div>

          {/* Breakdown Table */}
          <h3 style={{ fontSize: '16px', margin: '24px 0 12px' }}>Per-Client Breakdown</h3>
          <div className="ft-table-wrapper">
            <table className="ft-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Active Bots</th>
                  <th>Invoices</th>
                  <th>Total Paid</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No paid invoices this period</td></tr>
                ) : breakdown.map(row => (
                  <tr key={row.client_id}>
                    <td style={{ fontWeight: 500 }}>{row.company_name}</td>
                    <td>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                        color: row.visit_status === 'onboarded' || row.visit_status === 'active' ? '#22c55e' : '#f59e0b',
                        background: row.visit_status === 'onboarded' || row.visit_status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)' }}>
                        {row.visit_status || 'prospect'}
                      </span>
                    </td>
                    <td>{row.bot_count || 0}</td>
                    <td>{row.invoice_count}</td>
                    <td style={{ fontWeight: 600 }}>PKR {parseFloat(row.total_paid).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
